// Invoice state of an RMBL: its RHFL shipments with their costs, as the invoice page
// (crm/invoice) reads them. Used by the RMBL list (Consolidation.js) and the RMBL page's
// "Save & Invoice Details" (ConnectMBL.js).
import { buildCostParts } from "layouts/ecommerce/products/new-product/crm/invoice/finance/shared/costParts";

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const firstPositiveNumber = (...values) => {
    for (const value of values) {
        const number = toNumber(value);
        if (number > 0) return number;
    }

    return 0;
};

export const getInvoiceItemsTotalCost = (items = []) => {
    if (!Array.isArray(items)) return 0;

    return items.reduce(
        (sum, item) =>
            sum +
            firstPositiveNumber(
                item?.totalCost,
                item?.cost,
                item?.unitCost,
                item?.buyingRate
            ),
        0
    );
};

const getPricingTotalCost = (pricing) => {
    if (!pricing || typeof pricing !== "object") return 0;

    const items = Array.isArray(pricing) ? pricing : Object.values(pricing);
    return items.reduce(
        (sum, item) =>
            sum +
            firstPositiveNumber(
                item?.totalCost,
                item?.cost,
                item?.unitCost,
                item?.buyingRate
            ),
        0
    );
};

export const buildRmblInvoicePayload = (mblAttr) => {
    const shipments = mblAttr?.shipments || mblAttr?.shipmentDetails || [];
    if (!Array.isArray(shipments) || shipments.length === 0) return null;

    const formattedShipments = shipments.map((s, idx) => {
        const row = s?.attributes || s;
        const invoiceItems = Array.isArray(row.invoiceItems)
            ? row.invoiceItems
            : [];
        const rowFinancialSummary = row.financialSummary || {};
        const invoiceItemsTotalCost = getInvoiceItemsTotalCost(invoiceItems);
        const shipmentPricingTotalCost = getPricingTotalCost(row.shipmentPricing);

        const totalCost = firstPositiveNumber(
            row.totalCost,
            row.actualCostTotal,
            row.expectedCostTotal,
            row.operationalCost,
            // Shipment'in kendi maliyet alanları (CreateShipment.js'de kullanıcı
            // tarafından girilir); önceden bu liste dışındaydı ve tek shipment'lı
            // olmayan RMBL'lerde totalCost hep 0'a düşüyordu. Toplamları alınır:
            // tek tek denendiğinde ilk dolu olan (ör. alış) diğerlerini
            // (pickup, delivery, gümrük) düşürüyordu. RMBL sayfası da maliyeti aynı
            // toplamla hesaplıyor.
            toNumber(row.buyingRate) +
                toNumber(row.pickupRate) +
                toNumber(row.deliveryRate) +
                toNumber(row.exportCustomsRate) +
                toNumber(row.importCustomsRate),
            invoiceItemsTotalCost,
            shipmentPricingTotalCost,
            rowFinancialSummary.totalCost,
            rowFinancialSummary.shipmentCostTotal,
            rowFinancialSummary.buyingRate,
        );
        const costCurrency =
            row.buyingRateCurrency || rowFinancialSummary.totalCurrency || mblAttr?.financialSummary?.totalCurrency || "";

        return {
            shipmentNo: row.shipmentNo,
            sourceType: "road",
            customer: row.customer,
            sellingRateCurrency: row.sellingRateCurrency,
            sellingRate: row.sellingRate,
            finalSellingRate: row.finalSellingRate,
            buyingRateCurrency: row.buyingRateCurrency,
            costCurrency,
            totalCostCurrency: costCurrency,
            profit: row.profit || 0,
            totals: row.totals || {},
            carbonFootprint: row.carbonFootprint || {},
            invoiceItems,
            cargoDetails: row.cargoDetails || [],
            shipmentPricing: row.shipmentPricing || {},
            pricingDetails: row.pricingDetails || [],
            costItems: row.costItems || [],
            extraCosts: row.extraCosts || [],
            financialSummary: rowFinancialSummary,
            freightCharges: row.freightCharges || {},
            totalCost,
            actualCostTotal: totalCost,
            expectedCostTotal: totalCost,
            operationalCost: totalCost,
            totalCostIncludesBuyingRate: true,
            ...buildCostParts({
                sourceType: "road",
                row,
                mblAttr,
                shipmentId: s?.id || row._id || row.id,
                shipmentCount: shipments.length,
                masterNo: mblAttr?.rmblNumber,
            }),
            _id: row._id || row.id || `${idx}`,
        };
    });

    const linehaulServiceProviderMap = {};
    shipments.forEach((s) => {
        const row = s?.attributes || s;
        const key = `RHFL${row.shipmentNo || ""}`;
        linehaulServiceProviderMap[key] = row.serviceProvider || "N/A";
    });

    return { formattedShipments, linehaulServiceProviderMap };
};
